/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { validateSiteAddressInfo } from '@gh/builder-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  fieldStateByEntityConfig,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
} from '@gh/common-shared';
import { MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  foldAddressComponentsForXero,
  FormMode,
  MapboxService,
} from '@gh/core-ui';
import {
  buildEditableSelectColumn,
  EditableCellOptionSet,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';

import { foldToAsciiForXeroTrackingInputTrimmed } from '@gh/core-util';
import { assign, constant, isEmpty } from 'lodash';

import { RfqGeneral } from '../../resources/lead.model';
import {
  getInspectionItemId,
  hasInspectionAvailableValues,
  InspectionItem,
  RfqSite,
} from '../../resources/rfq.model';
import { SiteDetailsBusinessService } from '../../resources/site-details-business.service';
import { RfqSiteDetailsFormState } from './rfq-site-details-form.service';
import { RfqSiteMapDialogComponent } from './rfq-site-map-dialog.component';

@Component({
  standalone: false,
  selector: 'gh-lead-details-rfq-site-details',
  templateUrl: 'rfq-site-details.component.html',
  styleUrls: ['rfq-site-details.component.scss'],
  viewProviders: [
    RfqSiteDetailsFormState,
  ],
})

export class RfqSiteDetailsComponent extends AbstractFormComponent<RfqSite> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  generalInfo: RfqGeneral;
  siteDetails: RfqSite;
  gridDefinition: GridDefinition;
  mapCoordinates: number[] | null;
  showManualAddressFields = false;

  constructor(private route: ActivatedRoute,
    private translations: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private router: AppRouter,
    public form: RfqSiteDetailsFormState,
    private navigationService: BuilderNavigationService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private businessService: SiteDetailsBusinessService,
    private dialog: MatDialog,
    private mapboxService: MapboxService) {
    super();
  }

  get isAUorNZ(): boolean {
    return IS_REGION_AU_OR_NZ;
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_site_create',
      [FormMode.EditMode]: 'lbl_site_edit',
      [FormMode.ViewMode]: 'lbl_site_view',
    }[this.form.mode];
  }

  get hasValidCoordinates(): boolean {
    const latitudeControl = this.form.group.get('latitude');
    const longitudeControl = this.form.group.get('longitude');
    const latValue = latitudeControl?.value;
    const lngValue = longitudeControl?.value;

    return !!(latitudeControl?.valid && longitudeControl?.valid &&
      latValue != null && latValue !== '' &&
      lngValue != null && lngValue !== '');
  }

  ngOnInit(): void {
    const { siteDetails, salesFolderId, generalInfo } = getRouteInitData(this.route);

    assign(this, { siteDetails, salesFolderId, generalInfo });
    this.form.build(siteDetails, generalInfo);

    this.buildGridDefinition();

    const hasAddressData = !!(this.form.group.get('suburb')?.value ||
      this.form.group.get('streetNumber')?.value ||
      this.form.group.get('street')?.value ||
      this.form.group.get('postCode')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.onAddressChange();
    });
  }

  onCancel(): void {
    this.router.navigateBack();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_sites',
      entityType: 'SITE',
      entityId: this.siteDetails.id,
    });
  }

  onInspectedChanged($event: MatCheckboxChange): void {
    this.form.updateInspectedDate($event.checked);
  }

  onAddressChange() {
    const streetNumber = this.form.group.get('streetNumber')?.value || '';
    const street = this.form.group.get('street')?.value || '';
    const city = this.form.group.get('suburb')?.value || '';
    const postCode = this.form.group.get('postCode')?.value || '';

    const addressParts: string[] = [];
    if (streetNumber) addressParts.push(streetNumber);
    if (street) addressParts.push(street);
    if (city) addressParts.push(city);
    if (postCode) addressParts.push(postCode)

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) this.addressAutocomplete.inputValue = newAddress;

    const latitudeControl = this.form.group.get('latitude');
    const longitudeControl = this.form.group.get('longitude');

    if (latitudeControl?.value && longitudeControl?.value) {
      this.mapCoordinates = [longitudeControl.value, latitudeControl.value];
    } else {
      this.mapCoordinates = null;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    if (!components) return;

    const c = foldAddressComponentsForXero(components);

    this.form.group.patchValue({
      streetNumber: c.streetNo || '',
      street: c.street || '',
      suburb: c.city || '',
      postCode: c.postalCode || '',
    });

    if (c.coordinates) {
      const [lng, lat] = c.coordinates;
      const latitudeControl = this.form.group.get('latitude');
      const longitudeControl = this.form.group.get('longitude');

      if (latitudeControl && longitudeControl) {
        latitudeControl.setValue(lat);
        longitudeControl.setValue(lng);

        if (latitudeControl.valid && longitudeControl.valid && !isEmpty(c.coordinates)) {
          this.mapCoordinates = c.coordinates;
        } else {
          this.mapCoordinates = null;
          latitudeControl.setValue(null);
          longitudeControl.setValue(null);
        }
      }
    }

    ['streetNumber', 'street', 'suburb', 'postCode', 'latitude', 'longitude'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  onCoordinatesChanged(coordinates: [number, number]): void {
    this.mapboxService.reverseGeocode(coordinates).subscribe(result => {
      const feature = result?.features?.[0];
      if (!feature) return;
      const components = this.mapboxService.getUpdatedComponents(result);
      components.coordinates = coordinates.map(c => +c.toFixed(6)) as [number, number];
      this.onAddressSelected(components);
      if (this.addressAutocomplete) {
        this.addressAutocomplete.inputValue = foldToAsciiForXeroTrackingInputTrimmed(feature.place_name);
      }
    });
  }

  get completeFlow(): ActionFlow<FormFlowState<RfqSite>> {
    if (this.form.group.invalid) {
      this.checkAndShowManualAddressFields();
      this.form.group.markAllAsTouched();
    }
    return this.pipelines.complete({
      validate: this.commonActions.validate(
        (state: FormFlowState<RfqSite>) => this.validateBeforeProcess(state.value),
      ),
      execute: this.commonActions.execute((state: FormFlowState<RfqSite>) =>
        this.businessService.save(this.generalInfo.salesFolderId, state.form.submitValue)),
      finish: (state: FormFlowState<RfqSite>) => {
        const { generalInfo } = getRouteInitData(this.route);
        const { value } = state;

        if (this.form.createMode) {
          this.router.switchTo(['/sales/sales-folders/', this.generalInfo.salesFolderId, 'site', value.id]);
        } else {
          this.form.build(value, generalInfo);
        }
      },
    });
  }

  private validateBeforeProcess(site: RfqSite): Maybe<MessageBasket> {
    const errorId = validateSiteAddressInfo(site);
    return errorId ? MessageBasket.error(errorId) : void 0;
  }

  private checkAndShowManualAddressFields(): void {
    if (this.showManualAddressFields) return;

    const addressFields = ['streetNumber', 'street', 'suburb', 'postCode'];
    const hasAddressErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid;
    });

    if (hasAddressErrors) {
      this.showManualAddressFields = true;
    }
  }

  private buildGridDefinition(): void {
    const model = createDefaultGridModel<InspectionItem>({
      editable: this.form.editable,
      getId: getInspectionItemId,
      getCellDef: fieldStateByEntityConfig({ value: {} }),
      command: (entity, _, value) => this.form.setInspectionValue(getInspectionItemId(entity), value),
    });

    this.gridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        headerName: this.translations.get('lbl_title'),
        field: 'name',
        width: 200,
      }, {
        headerName: this.translations.get('lbl_selection'),
        field: 'value',
        width: 200,
        suppressSizeToFit: true,
        ...buildEditableSelectColumn(this.buildSelectionOptionSet(), model.getCellAccessor('value'), {
          hasAlternativeText: ({ data }) => !hasInspectionAvailableValues(data),
          getAlternativeText: constant(this.translations.get('lbl_no_available_values')),
        }),
      }],
    });
  }

  private buildSelectionOptionSet(): EditableCellOptionSet {
    return {
      format: (_, value) => value,
      getOptions: ({ data }: { data: InspectionItem }) =>
        data.availableValues.map((value) => ({ id: value, name: value })),
      hasOptions: ({ data }) => hasInspectionAvailableValues(data),
    };
  }

  isReferenceFieldVisible(): boolean {
    const referenceControl = this.form.group.get('reference');
    return referenceControl && (referenceControl as any)['visible'];
  }

  openMapDialog() {
    const dialogRef = this.dialog.open(RfqSiteMapDialogComponent, {
      width: '700px',
      data: { coordinates: this.mapCoordinates },
      panelClass: 'custom-map-dialog'
    });
    dialogRef.componentInstance.coordinatesChanged.subscribe((coords: [number, number]) => {
      this.onCoordinatesChanged(coords);
    });
  }
}
